import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { AppDrawer, AppDrawerProps } from "Components/Generic/AppDrawer";
import { Job } from "Models/job";
import { ReportChecklist } from "Models/report.checklist";
import { Error } from "Models/error";
import { Task } from "Models/task";
import { GenerateReportForm } from "./Form";
import JobReportService from "Services/job.report.service";
import { ExportReportType, ReportLanguage, ReportName } from "Util/enums";
import { JobOrderKey, JobReportKeys } from "Util/queries";
import { checkError } from "Util/checkError";
import { DrawerFooter } from "./Footer";
import { DrawerHeader } from "./Header";
import { useAppState } from "Context/StateContext";
import { MainComponent } from "../../modules/moving/components/Tasks/popups/CreateTask/menucard";
import OrderService from "Services/order.service";
import { Searches } from "Models/shipeezi.table";
import { WrapperSearchByOr } from "./styles";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { Reports } from "Models/job.report";

interface GenerateReportDrawerProps {}

export interface GenerateReportDrawerHandles {
  open: (order?: Job) => void;
  close: () => void;
}

export const GenerateReportDrawer = forwardRef<
  GenerateReportDrawerHandles,
  GenerateReportDrawerProps
>((_props, ref) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [order, setOrder] = useState<Job | undefined>(undefined);
  const [isWarningBannerOpened, setIsWarningBannerOpened] = useState<boolean>(false);

  const filters = useMemo(
    () => [
      {
        searchFor: "reference",
        label: translate("reference"),
      },
      {
        searchFor: "secondReference",
        label: translate("secondReference"),
      },
      {
        searchFor: "customer",
        label: translate("customer"),
      },
      {
        searchFor: "origins",
        label: translate("origin"),
      },
      {
        searchFor: "destinations",
        label: translate("destination"),
      },
    ],
    []
  );

  const [search, setSearch] = useState<Searches>([]);
  const [searchValue, setSearchValue] = useState("");
  const [searchFilter, setSearchFilter] = useState<string>(filters[0].searchFor);

  const [reportSelected, setReportSelected] = useState<ReportChecklist>();
  const [reportsSelected, setReportsSelected] = useState<ReportChecklist[]>([]);
  const [tasksSelected, setTasksSelected] = useState<Task[]>([]);
  const [sortReport, setSortReport] = useState<string>("");
  const [reportWithPhotos, setReportWithPhotos] = useState<number>();

  const { mutate: generateReport, isLoading: isGeneratingMultipleReports } = useMutation({
    mutationKey: [JobReportKeys.all],
    mutationFn: (generate: Reports) => JobReportService.generateMultipleReports(generate),
    onSuccess: (response) => {
      if (response?.data?.length) {
        const reportsLink = response.data?.[0]?.link;
        window.open(reportsLink, "_blank");
        handleClose();
        NotificationToast.success(translate("operationPerformedSuccessfully"));
      }
    },
    onError: (error: AxiosError<Error>) => {
      checkError(error, translate("anErrorOccurredTryAgain"));
    },
  });

  const { mutate: generateWorkOrderReport, isLoading: isGeneratingWorkOrderReport } = useMutation({
    mutationKey: [JobReportKeys.all],
    mutationFn: (generate: Reports) => JobReportService.generateWorkOrderReport(generate),
    onSuccess: (response) => {
      if (response.data) {
        setIsWarningBannerOpened(true);
        NotificationToast.success(translate("operationPerformedSuccessfully"));
      }
    },
    onError: (error: AxiosError<Error>) => {
      checkError(error, translate("anErrorOccurredTryAgain"));
    },
  });

  const { data: orderList, isLoading: isOrderListingLoading } = useQuery({
    queryKey: [JobOrderKey.all, searchValue, search],
    queryFn: async () =>
      await OrderService.getAll({
        page: 0,
        pageSize: 5,
        companyId: currentUser?.companyId,
        userId: currentUser?.id,
        searchByOr: search?.length ? JSON.stringify(search) : "",
      }).then(({ data }) => data),
    enabled:
      !!currentUser?.companyId && !!currentUser?.id && !order?.id && !!searchValue?.trim()?.length,
    initialData() {
      return [];
    },
  });

  const handleOpen = (newOrder?: Job) => {
    if (newOrder) setOrder(newOrder);
    setIsOpen(true);
  };

  const handleClearFields = () => {
    setSearchFilter(filters[0].searchFor);
    setSearch([]);
    setSearchValue("");

    setSortReport("");
    setReportSelected(undefined);
    setTasksSelected([]);
    setReportWithPhotos(undefined);
  };

  const handleClose = () => {
    handleClearFields();
    setOrder(undefined);

    setIsOpen(false);
  };

  const handleSubmit = () => {
    if (!reportSelected) return;

    if (reportSelected?.name?.toLowerCase() === ReportName.WORK_ORDER.toLowerCase()) {
      const generateJobReport = {
        items: [
          {
            jobId: order?.id!,
            type: ExportReportType.PDF,
            language: ReportLanguage.ENGLISH,
            preferences: currentUser?.preferences,
          },
        ],
      };

      generateWorkOrderReport(generateJobReport);

      return;
    }

    let checklistIds: number[];
    let taskIds: number[];
    let reportIds: number[];

    const tasksToReport = !Array.isArray(tasksSelected) ? [tasksSelected] : tasksSelected;

    if (!reportsSelected?.length) {
      checklistIds = reportSelected?.checklistId ? [reportSelected?.checklistId!] : [];
      reportIds = reportSelected?.reportId ? [reportSelected?.reportId!] : [];
      taskIds = tasksToReport
        .map((task) => task.id)
        ?.filter((value) => value !== null && value !== undefined) as number[];
    } else {
      checklistIds = reportsSelected
        .map((report) => report.checklistId)
        ?.filter((value) => value !== null && value !== undefined) as number[];
      taskIds = tasksToReport
        .map((task) => task.id)
        ?.filter((value) => value !== null && value !== undefined) as number[];
      reportIds = reportsSelected
        .map((report) => report.reportId)
        ?.filter((value) => value !== null && value !== undefined) as number[];
    }

    let generateJobReport = {
      items: [
        {
          jobId: order?.id!,
          type: ExportReportType.PDF,
          language: ReportLanguage.ENGLISH,
          includePhotos: Boolean(reportWithPhotos),
          preferences: currentUser?.preferences,
        },
      ],
    } as Reports;

    if (checklistIds?.length) {
      generateJobReport.items[0] = { ...generateJobReport.items[0], checklistIds };
    }

    if (taskIds?.length) {
      generateJobReport.items[0] = { ...generateJobReport.items[0], taskIds };
    }

    if (reportIds?.length) {
      generateJobReport.items[0] = { ...generateJobReport.items[0], reportIds };
    }

    if (sortReport?.trim()?.length) {
      generateJobReport.items[0] = { ...generateJobReport.items[0], groupBy: sortReport };
    }

    generateReport(generateJobReport);

    return;
  };

  const drawerSections: AppDrawerProps["sectionItems"] = [
    {
      id: "generatePDFreports",
      content: (
        <div>
          {window.location.href.toLowerCase().includes("analytics") && (
            <WrapperSearchByOr>
              <MainComponent
                boxWidth="100%"
                isLoading={isOrderListingLoading}
                options={orderList}
                onItemSelect={(job) => {
                  setOrder(job);
                  setReportSelected({
                    uuid: "workOrder",
                    name: ReportName.WORK_ORDER,
                  });
                }}
                searchQuery={searchValue}
                setSearchQuery={setSearchValue}
                filterQuery={searchFilter}
                setFilterQuery={setSearchFilter}
                filters={filters}
              />
            </WrapperSearchByOr>
          )}
          {!!order && !!Object.entries(order)?.length && (
            <>
              <DrawerHeader
                order={{ reference: order.reference, customer: order.customer, type: order.type }}
              />
              <GenerateReportForm
                order={order}
                tasks={tasksSelected}
                sortReport={sortReport}
                reportSelected={reportSelected}
                reportWithPhotos={reportWithPhotos}
                reportsSelected={reportsSelected}
                setTasks={setTasksSelected}
                setReportsSelected={setReportsSelected}
                setSortReport={setSortReport}
                setReportSelected={setReportSelected}
                setReportWithPhotos={setReportWithPhotos}
                isWarningBannerOpened={isWarningBannerOpened}
              />
            </>
          )}
        </div>
      ),
    },
  ];

  const isSubmitButtonDisabled = useMemo(() => {
    if (
      reportSelected?.name?.toLowerCase() === ReportName.ORDER_SUMMARY_REPORT.toLowerCase() ||
      reportSelected?.name?.toLowerCase() === ReportName.WORK_ORDER?.toLowerCase()
    )
      return false;

    if (reportSelected?.name?.toLowerCase() === ReportName.ALL_REPORTS.toLowerCase()) {
      const hasSelectedOrderSummary =
        reportsSelected.length === 1 &&
        !!reportsSelected.find(
          (report) => report.name?.toLowerCase() === ReportName.ORDER_SUMMARY_REPORT.toLowerCase()
        );

      if (hasSelectedOrderSummary) return false;

      if (reportsSelected?.length && tasksSelected?.length) return false;

      return true;
    }

    const tasksToReport = !Array.isArray(tasksSelected) ? [tasksSelected] : tasksSelected;

    if (tasksToReport?.length) return false;

    return true;
  }, [reportSelected, reportsSelected, tasksSelected]);

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  useEffect(() => {
    if (searchValue.length) {
      setSearch([
        {
          [searchFilter as string]: searchValue,
        },
      ]);

      return;
    }
    setSearch([]);
  }, [searchValue]);

  return (
    <AppDrawer
      DrawerProps={{
        open: isOpen,
        onClose: handleClose,
      }}
      title={translate("generatePDFreports")}
      hideSideMenu
      footer={
        <DrawerFooter
          isLoading={isGeneratingMultipleReports || isGeneratingWorkOrderReport}
          isDisabled={isSubmitButtonDisabled}
          submit={handleSubmit}
          onClose={handleClose}
        />
      }
      sectionItems={drawerSections}
    />
  );
});
